import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import Tracker from '@openreplay/tracker';
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import Loading from "components/Loading";
import { setMiniSidenav, useMaterialUIController } from "context";
import Sidenav from "examples/Sidenav";
import Cookies from "js-cookie";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Bounce, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import routes, { sidebarRoutes } from "routes";
import useAuthStore from "store/useAuthStore";
import { cookiesTokenName } from "utils/helper";
import SignIn from "./authentication/sign-in";
import './custom.css';

const tracker =
  process.env.REACT_APP_OPENREPLAY_KEY && process.env.REACT_APP_OPENREPLAY_URL
    ? new Tracker({
      projectKey: process.env.REACT_APP_OPENREPLAY_KEY,
      ingestPoint: process.env.REACT_APP_OPENREPLAY_URL,
      network: {
        capturePayload: true
      },
      __DISABLE_SECURE_MODE: process.env.NODE_ENV === 'development'
    })
    : null;

function ProtectedRoute({ children }) {
  const accessToken = Cookies.get(cookiesTokenName);
  return accessToken ? children : <Navigate to="/login" />;
}
ProtectedRoute.propTypes = { children: PropTypes.node.isRequired };

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, layout, openConfigurator, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setProfile, setToken, isSuperAdmin, permissions, profile, setNotificationsBadgeCount } = useAuthStore();
  const accessToken = Cookies.get(cookiesTokenName);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const hasOpenReplayConfig = process.env.REACT_APP_OPENREPLAY_KEY &&
      process.env.REACT_APP_OPENREPLAY_URL;

    const initializeTracker = async () => {
      if (tracker && hasOpenReplayConfig) {
        tracker.start();
      }
    };

    initializeTracker();
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsInitializing(true);
      const userData = localStorage.getItem('user');
      if (accessToken && userData) {
        setToken(accessToken);
        const user = JSON.parse(userData);
        setProfile(user);
        // getNotificationStats().then(
        //   (res) => {
        //     setNotificationsBadgeCount(res.data?.unread || 0);
        //   }
        // ).catch();

        // Only redirect to dashboard if user is on login page
        if (pathname === '/login') {
          navigate("/inspections-reports");
        }

        // if (typeof window !== 'undefined') {
        //   // Prevent re-initialization of OneSignal
        //   if (!window.__oneSignalInitialized) {
        //     await OneSignal.init({
        //       appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
        //       serviceWorkerPath: "/OneSignalSDKWorker.js",
        //       notifyButton: {
        //         enable: true,
        //       }
        //     });
        //     window.__oneSignalInitialized = true;
        //   }
        //   if (OneSignal.Notifications.isPushSupported()) {
        //     const permission = OneSignal.Notifications.permissionNative;
        //     if (permission !== 'granted') {
        //       console.log("OneSignal permission:", permission);
        //       // Request permission if not already granted
        //       OneSignal.Notifications.requestPermission();
        //     }
        //     OneSignal.User.PushSubscription.addEventListener("change", (event) => {
        //       console.log("Push subscription changed:", event.to);
        //       // event.to.id will be the new Player ID
        //     });
        //   }

        //   let playerId = OneSignal.User.PushSubscription.id;
        //   console.log("OneSignal Player ID:", playerId);
        //   if (playerId && localStorage.getItem(LOCAL_PLAYER_ID_KEY) !== playerId) {
        //     localStorage.setItem(LOCAL_PLAYER_ID_KEY, playerId);
        //     savePlayerId({ player_id: playerId, device_type: "web" });
        //   }
        // }

      } else if (!accessToken || !userData) {
        Cookies.remove(cookiesTokenName);
        if (pathname !== '/login') {
          navigate("/login");
        }
      }
      setIsInitializing(false);
    };

    initializeAuth();
    // eslint-disable-next-line
  }, [accessToken]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const filterRoutesByPermission = (routesArr) => {
    return routesArr
      .filter(route => !route.permission || permissions.includes(route.permission))
      .map(route => route.collapse
        ? { ...route, collapse: filterRoutesByPermission(route.collapse) }
        : route
      );
  };

  const filteredSidebarRoutes = filterRoutesByPermission(sidebarRoutes);
  const filteredRoutes = filterRoutesByPermission(routes);

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap(route =>
      route.collapse
        ? getRoutes(route.collapse)
        : route.route
          ? <Route
            key={route.key}
            path={route.route}
            element={route.route === '/login'
              ? route.component
              : <ProtectedRoute>{route.component}</ProtectedRoute>
            }
          />
          : []
    );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
        style={{ fontSize: '14px' }}
      />
      {isInitializing ?
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Loading size={50} />
        </div>
        : <>
          {layout === "dashboard" && (
            <Sidenav
              color={sidenavColor}
              brandName={isSuperAdmin ? "AI Admin Panel" : profile?.client?.name ?? "AI Admin Panel"}
              routes={[...filteredSidebarRoutes]}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          <Routes>
            {getRoutes([...filteredSidebarRoutes, ...filteredRoutes])}
            <Route path="/login" element={<SignIn />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </>}
    </ThemeProvider>
  );
}
